import { useResendCountdown } from '../../hooks/use-resend-countdown';
/**
 * Family Profile Picker
 * Netflix-style profile selection on a linked family device. It has no URL of
 * its own: app.tsx renders it automatically at the root when the browser
 * holds a family device token (local dev reaches it via the hub button).
 *  - Students: tap the photo → logged straight in (device token is the credential)
 *  - Guardians: tap the photo → a 6-digit access code is emailed to them and
 *    typed here — guardians ALWAYS enter through that code
 *  - Logout lives HERE (not in the portals): confirming it unlinks this device
 */

import { Modal } from '../../components/modal';
import { Button } from '../../components/button';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FamilyDeviceTokenError } from '../../api/family-device/errors';
import { useGetProfiles, useRequestGuardianCode, useGuardianCodeLogin, useUnlink } from '../../api/family-device/hooks';
import type {
  FamilyDeviceProfile,
  FamilyDeviceProfilesResponse,
} from '../../api/family-device/types';
import {
  clearFamilyDeviceToken,
  hasFamilyDeviceToken,
} from '../../services/family-device-storage';
import { authService } from '../../services/authentication/auth-service';
import { VersionFooter } from './version-footer';
import defaultAvatar from '../../assets/icons/avatars/default-avatar.svg';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import { FormMessage, Input } from '../../components/form-field';
import auth from './auth.module.css';
import picker from './family-profile-picker.module.css';

export function FamilyProfilePicker() {
  const getProfiles = useGetProfiles();
  const requestGuardianCode = useRequestGuardianCode();
  const unlink = useUnlink();
  const { t, i18n } = useTranslation();

  const [profiles, setProfiles] = useState<FamilyDeviceProfilesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [notLinked, setNotLinked] = useState(!hasFamilyDeviceToken());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guardian whose access-code prompt is open (null = closed)
  const [codeGuardian, setCodeGuardian] = useState<FamilyDeviceProfile | null>(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState('');

  useEffect(() => {
    if (notLinked) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProfiles()
      .then((data) => {
        if (!cancelled) setProfiles(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof FamilyDeviceTokenError) {
          // Token revoked or expired — the api helper already cleared it
          setNotLinked(true);
        } else {
          setError(t('familyLogin.errors.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notLinked, t, getProfiles, loadAttempt]);

  const goHome = () => {
    window.location.href = window.location.pathname;
  };

  const handlePickStudent = async (studentUserId: string) => {
    setBusyId(studentUserId);
    setError(null);
    try {
      const result = await authService.loginWithProfile(studentUserId);
      if (result.success) {
        goHome();
      } else if (!hasFamilyDeviceToken()) {
        setNotLinked(true);
      } else {
        setError(t('familyLogin.errors.loginFailed'));
      }
    } catch {
      setError(t('familyLogin.errors.loginFailed'));
    } finally {
      setBusyId(null);
    }
  };

  /** Guardians always enter via emailed access code — tapping requests one */
  const handlePickGuardian = async (guardian: FamilyDeviceProfile) => {
    setBusyId(guardian.userId);
    setError(null);
    try {
      const result = await requestGuardianCode(guardian.userId, i18n.language);
      setMaskedEmail(result.maskedEmail);
      setResendAvailableAt(result.resendAvailableAt);
      setCodeGuardian(guardian);
    } catch {
      setError(t('familyLogin.errors.linkFailed'));
    } finally {
      setBusyId(null);
    }
  };

  /** Confirmed logout: ends any session AND unlinks this device from the family */
  const handleLogout = async () => {
    if (!(await confirmDialog({ message: t('familyLogin.logoutConfirm'), tone: 'danger' }))) return;
    try {
      await unlink();
    } catch {
      // Token already invalid — proceed with the local cleanup anyway
    }
    clearFamilyDeviceToken();
    await authService.logout();
    goHome();
  };

  return (
    <div className={`${auth.loginContainer} portal-shell student-portal family-auth`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          <div className={auth.loginHeader}>
            <h1 className={auth.loginTitle}>{t('familyLogin.title')}</h1>
            <p className={auth.loginSubtitle}>{t('familyLogin.subtitle')}</p>
          </div>

          {loading && <p className={picker.familyPickerStatus}>{t('common.loading')}</p>}

          {error && <FormMessage>{error}</FormMessage>}

          {!loading && !notLinked && !profiles && error && (
            <Button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
              {t('familyLogin.retry')}
            </Button>
          )}

          {!loading && notLinked && (
            <div className={picker.familyPickerStatus}>
              <p>{t('familyLogin.notLinked')}</p>
              <a href={window.location.pathname} className={auth.loginSignupLink}>
                {t('familyLogin.backToHome')}
              </a>
            </div>
          )}

          {!loading && !notLinked && profiles && (
            <>
              {profiles.students.length === 0 && profiles.guardians.length === 0 ? (
                <p className={picker.familyPickerStatus}>{t('familyLogin.noProfiles')}</p>
              ) : (
                <div className={picker.familyPickerGrid}>
                  {profiles.students.map((student) => (
                    <button
                      key={student.userId}
                      type="button"
                      className={picker.familyPickerProfile}
                      onClick={() => void handlePickStudent(student.userId)}
                      disabled={busyId !== null}
                    >
                      <img
                        src={student.avatarPhotoUrl || defaultAvatar}
                        alt=""
                        className={picker.familyPickerAvatar}
                      />
                      <span className={picker.familyPickerName}>
                        {busyId === student.userId ? t('common.loading') : student.firstName}
                      </span>
                    </button>
                  ))}

                  {profiles.guardians.map((guardian) => (
                    <button
                      key={guardian.userId}
                      type="button"
                      className={picker.familyPickerProfile}
                      onClick={() => void handlePickGuardian(guardian)}
                      disabled={busyId !== null}
                    >
                      <img
                        src={guardian.avatarPhotoUrl || defaultAvatar}
                        alt=""
                        className={`${picker.familyPickerAvatar} ${picker.familyPickerAvatarGuardian}`}
                      />
                      <span className={picker.familyPickerName}>
                        {busyId === guardian.userId ? t('common.loading') : guardian.firstName}
                      </span>
                      <span className={picker.familyPickerRole}>✉️ {t('familyLogin.guardianRole')}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className={picker.familyPickerFooter}>
                <button type="button" className={picker.familyPickerLogout} onClick={() => void handleLogout()}>
                  {t('familyLogin.logout')}
                </button>
              </div>
            </>
          )}
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>

      {codeGuardian && (
        <GuardianCodePrompt
          guardian={codeGuardian}
          maskedEmail={maskedEmail}
          initialResendAvailableAt={resendAvailableAt}
          onClose={() => setCodeGuardian(null)}
        />
      )}
    </div>
  );
}

/** Modal asking the 6-digit access code emailed to the guardian */
function GuardianCodePrompt({
  guardian,
  maskedEmail,
  initialResendAvailableAt,
  onClose,
}: {
  guardian: FamilyDeviceProfile;
  maskedEmail: string;
  initialResendAvailableAt: string;
  onClose: () => void;
}) {
  const requestGuardianCode = useRequestGuardianCode();
  const guardianCodeLogin = useGuardianCodeLogin();
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resend cooldown countdown (seconds remaining)
  const [resendAt, setResendAt] = useState<Date>(new Date(initialResendAvailableAt));
  const resendIn = useResendCountdown(resendAt);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await guardianCodeLogin(guardian.userId, code);
      authService.adoptLoginResponse(response);
      window.location.href = window.location.pathname;
    } catch {
      setError(t('loginCode.invalid'));
      setCode('');
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await requestGuardianCode(guardian.userId, i18n.language);
      setResendAt(new Date(result.resendAvailableAt));
    } catch {
      setError(t('familyLogin.errors.linkFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => !busy && onClose()}
      size="sm"
      framed
      hideClose
      disableBackdropClose
    >
      <div className={picker.familyPinCard}>
        <img
          src={guardian.avatarPhotoUrl || defaultAvatar}
          alt=""
          className={`${picker.familyPickerAvatar} ${picker.familyPickerAvatarGuardian}`}
        />
        <h2 className={picker.familyPinTitle}>{t('loginCode.promptTitle', { name: guardian.firstName })}</h2>
        <p className={picker.familyPinHint}>{t('loginCode.hint', { email: maskedEmail })}</p>

        <form onSubmit={(e) => void handleSubmit(e)}>
          <Input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={picker.familyCodeInput}
            placeholder="000000"
            autoFocus
            disabled={busy}
            autoComplete="one-time-code"
          />
          {error && <FormMessage>{error}</FormMessage>}
          <div className={picker.familyPinActions}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={code.length !== 6 || busy}>
              {busy ? t('common.loading') : t('loginCode.submit')}
            </Button>
          </div>
        </form>

        <button
          type="button"
          className={auth.signupResend}
          onClick={() => void handleResend()}
          disabled={busy || resendIn > 0}
        >
          {resendIn > 0 ? t('signup.resendIn', { seconds: resendIn }) : t('signup.resendCode')}
        </button>
      </div>
    </Modal>
  );
}
